import { useState } from "react";
import { Link } from "react-router-dom";
import { Copy, Flame, KeyRound, RefreshCw, ShieldCheck, Archive, RotateCcw, Trash2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";

export default function MasterAdmin() {
  const [key, setKey] = useState("");
  const [tenants, setTenants] = useState(null);
  const [busy, setBusy] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = async (k = key) => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/tenants", { headers: { "X-Admin-Key": k } });
      setTenants(data);
    } catch (err) {
      setTenants(null);
      toast.error(formatApiError(err, "Invalid admin key"));
    } finally {
      setBusy(false);
    }
  };

  const copy = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  const archiveToggle = async (r) => {
    if (!r.archived && !window.confirm(`Archive "${r.name}"? Its staff and managers immediately lose access until you restore it. No data is deleted.`)) return;
    setBusyId(r.id);
    try {
      await api.post(`/admin/tenants/${r.id}/${r.archived ? "restore" : "archive"}`, {}, { headers: { "X-Admin-Key": key } });
      toast.success(r.archived ? `"${r.name}" restored` : `"${r.name}" archived`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Action failed"));
    } finally {
      setBusyId(null);
    }
  };

  const deleteTenant = async (r) => {
    const answer = window.prompt(`PERMANENTLY delete "${r.name}"?\n\nAll staff and manager accounts, templates, shifts, task history and photo records will be erased forever. This cannot be undone.\n\nType the business name exactly to confirm:`);
    if (answer === null) return;
    if (answer.trim() !== r.name) {
      toast.error("Name didn't match — nothing was deleted");
      return;
    }
    setBusyId(r.id);
    try {
      await api.delete(`/admin/tenants/${r.id}`, { headers: { "X-Admin-Key": key } });
      toast.success(`"${r.name}" permanently deleted`);
      await load();
    } catch (err) {
      toast.error(formatApiError(err, "Delete failed"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FBF8F3] px-4 sm:px-8 py-10">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center gap-3 mb-2">
          <img src="/jotprep-icon.png" alt="jotprep" className="w-10 h-10 object-contain" />
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#1A1D20]">Master View</h1>
        </div>
        <p className="text-sm text-slate-500 mb-8">Directory of every business workspace. Archive a business to suspend access, or delete it permanently.</p>

        <form
          onSubmit={(e) => { e.preventDefault(); load(); }}
          className="flex gap-2 mb-8"
          data-testid="master-key-form"
        >
          <div className="relative flex-1 max-w-md">
            <KeyRound className="w-4 h-4 text-slate-500 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="Master admin key"
              data-testid="master-key-input"
              className="w-full h-12 pl-11 pr-4 rounded-xl bg-[#FAF6F0] border border-[#E4DDD3] text-[#1A1D20] placeholder-slate-400 focus:outline-none focus:border-terra-400/60"
            />
          </div>
          <button
            type="submit"
            disabled={busy || !key}
            data-testid="master-load-button"
            className="h-12 px-5 rounded-xl bg-terra-500 text-white font-display font-bold hover:bg-terra-600 disabled:opacity-50 transition-colors"
          >
            {busy ? "Loading…" : "View"}
          </button>
          {tenants && (
            <button
              type="button"
              onClick={() => load()}
              className="h-12 px-4 rounded-xl border border-[#E4DDD3] text-slate-500 hover:text-terra-600 transition-colors"
              data-testid="master-refresh-button"
              title="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
          )}
        </form>

        {tenants && (
          <div className="space-y-4" data-testid="tenants-list">
            <div className="text-sm text-slate-500">{tenants.length} business{tenants.length === 1 ? "" : "es"}</div>
            {tenants.map((r) => (
              <TenantCard key={r.id} r={r} busy={busyId === r.id} onCopy={copy} onArchive={archiveToggle} onDelete={deleteTenant} />
            ))}
          </div>
        )}

        <div className="mt-10">
          <Link to="/" className="text-sm text-slate-500 hover:text-terra-600 transition-colors" data-testid="master-back-link">
            ← Back to app
          </Link>
        </div>
      </div>
    </div>
  );
}

function TenantCard({ r, busy, onCopy, onArchive, onDelete }) {
  return (
    <div className="p-5 rounded-2xl bg-white border border-[#E4DDD3]" data-testid={`tenant-card-${r.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display font-semibold text-lg text-[#1A1D20]">{r.name}</div>
            {r.archived && (
              <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 border border-slate-300 text-slate-500 uppercase tracking-wider font-bold" data-testid={`tenant-archived-badge-${r.id}`}>
                Archived
              </span>
            )}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Created {new Date(r.created_at).toLocaleDateString()} · Day starts {r.business_day_start || "—"} · {r.windows.length} windows
          </div>
        </div>
        <button
          onClick={() => onCopy(r.join_code, "Join code")}
          data-testid={`tenant-code-copy-${r.id}`}
          className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#FDF0EB] border border-terra-400/40 text-terra-600 font-mono font-bold tracking-[0.2em] hover:border-terra-400 transition-colors"
        >
          {r.join_code}
          <Copy className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="mt-4 flex gap-6 flex-wrap text-sm">
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Managers</div>
          {r.managers.length === 0 ? (
            <span className="text-slate-500">—</span>
          ) : (
            r.managers.map((m) => (
              <div key={m.email || m.name} className="text-slate-700">
                {m.name} <span className="text-slate-500">{m.email}</span>
              </div>
            ))
          )}
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Staff</div>
          <span className="text-slate-700">{r.staff_count}</span>
        </div>
        <div>
          <div className="text-xs text-slate-500 uppercase tracking-wider mb-1">Templates</div>
          <span className="text-slate-700">{r.templates_count}</span>
        </div>
      </div>
      <div className="mt-4 pt-4 border-t border-[#E4DDD3]/60 flex gap-2 justify-end">
        <button
          onClick={() => onArchive(r)}
          disabled={busy}
          data-testid={`tenant-archive-button-${r.id}`}
          className="h-9 px-3 rounded-lg border border-[#E4DDD3] text-slate-600 text-xs font-medium hover:border-terra-400/50 hover:text-terra-600 disabled:opacity-50 transition-colors flex items-center gap-1.5"
        >
          {r.archived ? <RotateCcw className="w-3.5 h-3.5" /> : <Archive className="w-3.5 h-3.5" />}
          {r.archived ? "Restore" : "Archive"}
        </button>
        <button
          onClick={() => onDelete(r)}
          disabled={busy}
          data-testid={`tenant-delete-button-${r.id}`}
          className="h-9 px-3 rounded-lg border border-rose-300/40 text-rose-600 text-xs font-medium hover:bg-rose-50 disabled:opacity-50 transition-colors flex items-center gap-1.5"
        >
          <Trash2 className="w-3.5 h-3.5" /> Delete Permanently
        </button>
      </div>
    </div>
  );
}
